import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Plus,
    FileText,
    Server,
    Search,
    Trash2,
    Check,
    Copy,
    ChevronRight,
    Mail
} from 'lucide-react';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';
import { useEmailTemplates, type EmailTemplate } from '@/store/email-template';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { CloneTemplateModal } from './clone-template-modal';
import { CreateTemplateModal } from './create-template-modal';
import { Card } from '@/components/ui/Card';

const DEFAULT_SYSTEM_VARIABLES = [
    { key: 'to', label: 'Primary Recipient', default_value: 'recipient@example.com', description: 'Target email address', required: true },
    { key: 'cc', label: 'Carbon Copy', default_value: 'cc@example.com', description: 'CC recipient addresses', required: false },
    { key: 'bcc', label: 'Blind Carbon Copy', default_value: 'bcc@example.com', description: 'BCC recipient addresses', required: false },
    { key: 'subject', label: 'Email Subject', default_value: 'Notification Update', description: 'Email subject text', required: true },
];

export const MailPage: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const {
        templates,
        isLoading,
        fetchTemplates,
        deleteTemplate,
        cloneTemplate,
        createTemplate,
    } = useEmailTemplates();

    const [searchQuery, setSearchQuery] = useState('');
    const [templateToDelete, setTemplateToDelete] = useState<{ id: string; name: string } | null>(null);
    const [templateToClone, setTemplateToClone] = useState<EmailTemplate | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Services' },
            { name: 'Email' }
        ]);
        fetchTemplates();
    }, [setBreadcrumbs, fetchTemplates]);

    const handleCopyEndpoint = (key: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const url = `${window.location.origin}/api/svc/v1/email/${key}/send`;
        navigator.clipboard.writeText(url);
        setCopiedKey(key);
        toast.success(`Copied invocation URL for ${key}`);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleCreateTemplate = async (name: string, key: string) => {
        const created = await createTemplate({
            name,
            key,
            description: '',
            subject: 'Notification Update',
            html_content: '<p>Hello {{to}},</p><p>This is a new email template.</p>',
            variables: DEFAULT_SYSTEM_VARIABLES,
            is_active: true,
        });
        if (created && created.id) {
            navigate(`/m/mail/${created.id}`);
        }
    };

    const [selectedTag, setSelectedTag] = useState<string>('all');

    // Collect unique tags from all templates
    const allTags = Array.from(new Set(templates.flatMap((t: EmailTemplate) => t.tags || [])));

    const filteredTemplates = templates.filter((t: EmailTemplate) => {
        const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));
        
        const matchesTag = selectedTag === 'all' || (t.tags && t.tags.includes(selectedTag));
        return matchesSearch && matchesTag;
    });

    return (
        <PageLayout
            title="Email Templates"
            description="Manage HTML email templates backed by Azure Logic Apps connections."
            action={
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/m/configurations/email')}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        <Server className="w-4 h-4 text-slate-400" />
                        Manage Providers
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) rounded-lg transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        New Email Template
                    </button>
                </div>
            }
        >
            <div className="space-y-6 animate-in fade-in duration-300">

                {/* Search & Tag Filter Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="relative w-full sm:w-80">
                        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search email templates..."
                            className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 text-slate-900 dark:text-white"
                        />
                    </div>

                    {/* Tag Filter Pills */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                            type="button"
                            onClick={() => setSelectedTag('all')}
                            className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${selectedTag === 'all'
                                ? 'bg-(--primary) text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            All ({templates.length})
                        </button>
                        {allTags.map(tag => (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => setSelectedTag(tag)}
                                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors capitalize ${selectedTag === tag
                                    ? 'bg-(--primary) text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* EMAIL TEMPLATES GRID */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-(--primary)"></div>
                    </div>
                ) : filteredTemplates.length === 0 ? (
                    <Card className="text-center py-16">
                        <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
                        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200">No Email Templates Found</h3>
                        <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1 mb-6">
                            {searchQuery || selectedTag !== 'all' ? 'No templates match your filter query.' : 'Create your first email template to send automated messages via Logic Apps.'}
                        </p>
                        {!searchQuery && selectedTag === 'all' && (
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) rounded-lg shadow-sm"
                            >
                                <Plus className="w-4 h-4" /> Create Email Template
                            </button>
                        )}
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTemplates.map((template: EmailTemplate) => (
                            <div
                                key={template.id}
                                onClick={() => navigate(`/m/mail/${template.id}`)}
                                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:shadow-md hover:border-(--primary)/40 transition-all duration-200 cursor-pointer flex flex-col justify-between"
                            >
                                <div>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-950/50 text-(--primary) flex items-center justify-center shrink-0">
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateToClone(template);
                                                }}
                                                className="p-1.5 text-slate-400 hover:text-(--primary) hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                                                title="Clone Template"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateToDelete({ id: template.id, name: template.name });
                                                }}
                                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Template"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <h4 className="font-bold text-slate-900 dark:text-white group-hover:text-(--primary) transition-colors text-base line-clamp-1">
                                        {template.name}
                                    </h4>

                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        <span className="font-mono text-xs px-2.5 py-0.5 rounded-md bg-sky-50 text-(--primary) dark:bg-sky-950/60 dark:text-sky-300 border border-sky-100 dark:border-sky-900 font-semibold">
                                            {template.key}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${template.is_active
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                            }`}>
                                            {template.is_active ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </div>

                                    {/* Template Tags & Event Badges */}
                                    <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                                        {(template.tags || []).map(t => (
                                            <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded bg-violet-50 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 border border-violet-100 dark:border-violet-900 uppercase">
                                                {t}
                                            </span>
                                        ))}
                                        {template.event_ids && template.event_ids.length > 0 && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-100 dark:border-blue-900">
                                                {template.event_ids.length} event trigger{template.event_ids.length > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 line-clamp-2 leading-relaxed">
                                        {template.description || 'No description provided.'}
                                    </p>
                                </div>

                                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800/80 space-y-3">
                                    {/* Invocation Endpoint banner for active templates */}
                                    {template.is_active && (
                                        <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 rounded-lg text-xs font-mono">
                                            <span className="text-slate-500 dark:text-slate-400 truncate mr-2">
                                                POST /api/svc/v1/email/{template.key}/send
                                            </span>
                                            <button
                                                type="button"
                                                onClick={(e) => handleCopyEndpoint(template.key, e)}
                                                className="shrink-0 p-1 text-slate-400 hover:text-(--primary) transition-colors"
                                                title="Copy invocation URL"
                                            >
                                                {copiedKey === template.key ? (
                                                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                                                ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between text-xs text-slate-400">
                                        <span>{template.variables?.length || 0} variables</span>
                                        <span className="flex items-center gap-1 font-medium text-(--primary) group-hover:translate-x-0.5 transition-transform">
                                            Manage Template <ChevronRight className="w-3.5 h-3.5" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Template Modal */}
                <CreateTemplateModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onConfirm={handleCreateTemplate}
                />

                {/* Delete Confirmation Modal */}
                <ConfirmDeleteModal
                    isOpen={!!templateToDelete}
                    onClose={() => setTemplateToDelete(null)}
                    onConfirm={async () => {
                        if (templateToDelete) {
                            await deleteTemplate(templateToDelete.id);
                            toast.success(`Deleted template "${templateToDelete.name}"`);
                            setTemplateToDelete(null);
                        }
                    }}
                    title="Delete Email Template"
                    itemName={templateToDelete?.name || ''}
                    description="Are you sure you want to delete this email template? Applications using this template key will fail to render emails."
                />

                {/* Clone Template Modal */}
                <CloneTemplateModal
                    isOpen={!!templateToClone}
                    onClose={() => setTemplateToClone(null)}
                    sourceName={templateToClone?.name || ''}
                    sourceKey={templateToClone?.key || ''}
                    onConfirm={async (newName, newKey) => {
                        if (templateToClone) {
                            await cloneTemplate(templateToClone.id, newName, newKey);
                            setTemplateToClone(null);
                        }
                    }}
                />
            </div>
        </PageLayout>
    );
};
